import * as Actions from "../index";
import * as TYPES from "../actions";
import { listPackages } from "../../../api/queries";
import { listUserPackages } from "../../../api/queries";
import { listStudentPackages } from "../../../api/queries";
import { createFreePackage } from "../../../api/mutations";
import { API, graphqlOperation } from "aws-amplify";
import { sortByDate } from "../../../common";

const DIAGNOSTIC_TYPES = ["Diagnostic", "Diagonostic"];
const ENSURE_COOLDOWN_MS = 8000;
const ensureInFlight = new Map();
const ensureLastRunAt = new Map();

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const runEnsureOnce = (key, fn) => {
  if (!key) return Promise.resolve();
  if (ensureInFlight.has(key)) return ensureInFlight.get(key);
  const last = ensureLastRunAt.get(key);
  const now = Date.now();
  if (last && now - last < ENSURE_COOLDOWN_MS) return wait(1200);

  const p = (async () => {
    ensureLastRunAt.set(key, Date.now());
    try {
      return await fn();
    } finally {
      ensureInFlight.delete(key);
    }
  })();

  ensureInFlight.set(key, p);
  return p;
};

const getDiagnosticPackage = async () => {
  const result = await API.graphql(
    graphqlOperation(listPackages, {
      filter: {
        or: DIAGNOSTIC_TYPES.map((type) => ({
          type: { eq: type },
        })),
      },
      limit: 100,
    })
  );

  const items = result?.data?.listPackages?.items || [];
  if (!items.length) return null;

  const preferred = items.find((pkg) => pkg?.price === 0 || pkg?.cnprice === 0);
  return preferred || items[0];
};

const getFirstPackageQuizId = (pkg) => {
  return pkg?.packageItems?.items?.[0]?.quiz?.id || pkg?.packageItems?.items?.[0]?.quizId;
};

const hasDiagnosticUserPackage = (userPackages, diagnosticPackageId) => {
  return (userPackages || []).some((userPackage) => {
    const packageType = userPackage?.package?.type;
    if (packageType && DIAGNOSTIC_TYPES.includes(packageType)) return true;
    if (diagnosticPackageId && userPackage?.packageId === diagnosticPackageId) return true;
    return false;
  });
};

export const selectedCourseForRegistration = (userData) => {
  return {
    type: TYPES.SELECT_COURSE_FOR_CART,
    data: userData,
  };
};

export const saveAllCourses = (userData) => {
  return {
    type: TYPES.SAVE_COURSES,
    data: userData,
  };
};

export const saveUserCourses = (userData) => {
  return {
    type: TYPES.SAVE_MY_COURSE,
    data: userData,
  };
};

export const listAllCourses = () => {
  return (dispatch) => {
    try {
      dispatch({
        type: TYPES.UI_START_LOADING,
      });
      return API.graphql({ query: listPackages }).then((result) => {
        dispatch({
          type: TYPES.UI_STOP_LOADING,
        });
        result?.data?.listPackages?.items?.sort((a, b) => {
          if (a?.alias && b?.alias) {
            const nameA = a?.alias.toUpperCase(); // Convert names to uppercase for case-insensitive sorting
            const nameB = b?.alias.toUpperCase();
            if (nameA < nameB) {
              return -1; // a should come before b in the sorted order
            }
            if (nameA > nameB) {
              return 1; // a should come after b in the sorted order
            }
            return 0; // names are equal
          }
        });
        // console.log("Sorted is ", sorted)
        dispatch(Actions.saveAllCourses(result.data.listPackages.items));
        return result;
      });
    } catch (err) {
      dispatch({
        type: TYPES.UI_STOP_LOADING,
      });
      console.log("Error in dispatch is ", err);
    }
  };
};

export const ensureDiagnosticUserPackageForStudent = (studentId) => {
  return async () =>
    runEnsureOnce(`student:${studentId}`, async () => {
      try {
        if (!studentId) return;

        const diagnosticPackage = await getDiagnosticPackage();
        if (!diagnosticPackage?.id) return;

        const diagnosticQuizId = getFirstPackageQuizId(diagnosticPackage);
        if (!diagnosticQuizId) return;

        const currentStudentPackagesResult = await API.graphql(
          graphqlOperation(listStudentPackages, {
            studentId,
            sortDirection: "DESC",
            limit: 1000,
          })
        );
        const currentStudentPackages =
          currentStudentPackagesResult?.data?.listStudentPackages?.items || [];

        if (hasDiagnosticUserPackage(currentStudentPackages, diagnosticPackage?.id)) return;

        await API.graphql(
          graphqlOperation(createFreePackage, {
            packageId: diagnosticPackage.id,
            userId: studentId,
            quizId: diagnosticQuizId,
          })
        );
      } catch (err) {
        console.log("Error in ensureDiagnosticUserPackageForStudent is ", err);
      }
    });
};

export const ensureDiagnosticUserPackageForParent = (userId) => {
  return async () =>
    runEnsureOnce(`parent:${userId}`, async () => {
      try {
        if (!userId) return;

        const diagnosticPackage = await getDiagnosticPackage();
        if (!diagnosticPackage?.id) return;

        const diagnosticQuizId = getFirstPackageQuizId(diagnosticPackage);
        if (!diagnosticQuizId) return;

        const currentUserPackagesResult = await API.graphql(
          graphqlOperation(listUserPackages, {
            userId,
            sortDirection: "DESC",
            limit: 1000,
          })
        );
        const currentUserPackages =
          currentUserPackagesResult?.data?.listUserPackages?.items || [];

        if (hasDiagnosticUserPackage(currentUserPackages, diagnosticPackage?.id)) return;

        await API.graphql(
          graphqlOperation(createFreePackage, {
            packageId: diagnosticPackage.id,
            userId,
            quizId: diagnosticQuizId,
          })
        );
      } catch (err) {
        console.log("Error in ensureDiagnosticUserPackageForParent is ", err);
      }
    });
};

export const dispatchMyCousesForParent = (userData) => {
  return (dispatch) => {
    try {
      dispatch({
        type: TYPES.UI_START_LOADING,
      });
      return API.graphql(graphqlOperation(listUserPackages, userData)).then(async (result) => {
        const userId = userData?.userId;
        const items = result?.data?.listUserPackages?.items || [];

        if (userId && !hasDiagnosticUserPackage(items)) {
          await dispatch(ensureDiagnosticUserPackageForParent(userId));
          result = await API.graphql(graphqlOperation(listUserPackages, userData));
        }

        const courses = await sortByDate(result?.data?.listUserPackages?.items);
        dispatch(Actions.saveUserCourses(courses));
        dispatch({
          type: TYPES.UI_STOP_LOADING,
        });
        return result;
      });
    } catch (err) {
      dispatch({
        type: TYPES.UI_STOP_LOADING,
      });
      console.log("Error in dispatch is ", err);
    }
  };
};

export const dispatchMyCousesForStudent = (userData) => {
  return (dispatch) => {
    try {
      dispatch({
        type: TYPES.UI_START_LOADING,
      });
      return API.graphql(graphqlOperation(listStudentPackages, userData)).then(async (result) => {
        const studentId = userData?.studentId;
        const items = result?.data?.listStudentPackages?.items || [];

        if (studentId && !hasDiagnosticUserPackage(items)) {
          await dispatch(ensureDiagnosticUserPackageForStudent(studentId));
          result = await API.graphql(graphqlOperation(listStudentPackages, userData));
        }

        dispatch(Actions.saveUserCourses(result?.data?.listStudentPackages?.items));
        dispatch({
          type: TYPES.UI_STOP_LOADING,
        });
        return result;
      });
    } catch (err) {
      dispatch({
        type: TYPES.UI_STOP_LOADING,
      });
      console.log("Error in dispatch is ", err);
    }
  };
};
